/**
 * Utilidades centralizadas para fechas de servicio.
 *
 * El backend guarda fechas de servicio como ISO a medianoche UTC
 * (ej. "2025-05-26T00:00:00.000Z" representa el 26 de mayo, no el 25).
 * Siempre usar getters UTC o timeZone: "UTC" al mostrar/filtrar fechas de servicio.
 */

export const parseServiceDate = (isoOrDate) => {
  if (!isoOrDate) return null;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

/** ISO → yyyy-mm-dd para inputs type="date" */
export const formatDateInput = (isoOrDate) => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** yyyy-mm-dd → ISO medianoche UTC */
export const dateToISO = (dateString) => {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length !== 3) return "";
  const [yyyy, mm, dd] = parts.map(Number);
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

/** Construye ISO desde input yyyy-mm-dd o número de día del mes visible */
export const buildIsoFromDateInput = (dateInput, dayNumberFallback, currentDate) => {
  if (dateInput) {
    const iso = dateToISO(dateInput);
    if (iso) return iso;
  }
  if (typeof dayNumberFallback === "number" && currentDate) {
    const d = new Date(
      Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        dayNumberFallback
      )
    );
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return null;
};

/** Formatea fecha de servicio para mostrar (día calendario UTC) */
export const formatServiceDate = (isoOrDate, locale = "es-MX", options = {}) => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return "—";
  return d.toLocaleDateString(locale, {
    timeZone: "UTC",
    ...options,
  });
};

export const formatServiceDateShort = (isoOrDate, locale = "es-MX") =>
  formatServiceDate(isoOrDate, locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export const formatServiceDateSlash = (isoOrDate) => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return null;
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(
    d.getUTCMonth() + 1
  ).padStart(2, "0")}/${d.getUTCFullYear()}`;
};

export const getServiceYearMonth = (isoOrDate) => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return null;
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
  };
};

export const getMonthYearKey = (isoOrDate) => {
  const ym = getServiceYearMonth(isoOrDate);
  if (!ym) return null;
  return `${ym.year}-${String(ym.month).padStart(2, "0")}`;
};

export const isServiceDateInMonth = (isoOrDate, year, month) => {
  const ym = getServiceYearMonth(isoOrDate);
  if (!ym) return false;
  return ym.year === year && ym.month === month;
};

export const getServiceDayNumber = (isoOrDate) => {
  const d = parseServiceDate(isoOrDate);
  return d ? d.getUTCDate() : null;
};

/**
 * El día operativo cambia a las 5:00 AM (no a medianoche).
 * Los técnicos trabajan de madrugada y siguen en el turno / procedia del día anterior.
 */
export const BUSINESS_DAY_CUTOFF_HOUR = 5;

export const getBusinessDateParts = (date = new Date()) => {
  const shifted = new Date(
    date.getTime() - BUSINESS_DAY_CUTOFF_HOUR * 60 * 60 * 1000
  );
  return {
    year: shifted.getFullYear(),
    month: shifted.getMonth(),
    day: shifted.getDate(),
  };
};

/** Mes operativo actual (1–12), alineado con el backend */
export const getBusinessYearMonth = (date = new Date()) => {
  const { year, month } = getBusinessDateParts(date);
  return { year, month: month + 1 };
};

/** true si el mes calendario (year, month 1–12) es posterior al mes operativo */
export const isFutureBusinessMonth = (year, month, date = new Date()) => {
  const biz = getBusinessYearMonth(date);
  return year > biz.year || (year === biz.year && month > biz.month);
};

/** Orden de turno: 05:00…23:59 y luego 00:00…04:59 */
export const hourToBusinessMinutes = (hour) => {
  if (!hour) return 48 * 60;
  const [hh, mm] = String(hour).split(":").map(Number);
  const h = Number.isNaN(hh) ? 0 : hh;
  const m = Number.isNaN(mm) ? 0 : mm;
  const mins = h * 60 + m;
  if (mins < BUSINESS_DAY_CUTOFF_HOUR * 60) return mins + 24 * 60;
  return mins;
};

export const isOvernightHour = (hour) => {
  if (!hour) return false;
  const [hh, mm] = String(hour).split(":").map(Number);
  if (Number.isNaN(hh)) return false;
  return hh * 60 + (Number.isNaN(mm) ? 0 : mm) < BUSINESS_DAY_CUTOFF_HOUR * 60;
};

/** Arma un Date HH:mm respetando el turno que corta a las 5:00 */
export const resolveBusinessDateTime = (timeHHmm, now = new Date()) => {
  const [hh, mm] = String(timeHHmm || "").split(":").map(Number);
  const h = Number.isNaN(hh) ? 0 : hh;
  const m = Number.isNaN(mm) ? 0 : mm;
  const result = new Date(now);
  result.setHours(h, m, 0, 0);
  if (now.getHours() < BUSINESS_DAY_CUTOFF_HOUR && h >= BUSINESS_DAY_CUTOFF_HOUR) {
    result.setDate(result.getDate() - 1);
  }
  return result;
};

/** Medianoche UTC del día operativo de hoy (cambia a las 5:00 AM) */
export const getTodayUtcMs = () => {
  const { year, month, day } = getBusinessDateParts();
  return Date.UTC(year, month, day);
};

export const getLocalYmd = (date = new Date()) => {
  const { year, month, day } = getBusinessDateParts(date);
  const yyyy = year;
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const MEXICO_TZ = "America/Mexico_City";

export const getZonedYearMonth = (isoOrDate, timeZone = "UTC") => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return null;
  if (timeZone === "UTC") {
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
  }).formatToParts(d);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  if (!year || !month) return null;
  return { year, month };
};

/** Mes/año de una visita (entryTime real) en zona México */
export const getVisitMonthYearKey = (isoOrDate) => {
  const ym = getZonedYearMonth(isoOrDate, MEXICO_TZ);
  if (!ym) return null;
  return `${ym.year}-${String(ym.month).padStart(2, "0")}`;
};

export const getServiceDayUtcMs = (isoOrDate) => {
  const d = parseServiceDate(isoOrDate);
  if (!d) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export const isServiceDateBeforeToday = (isoOrDate) => {
  const dayMs = getServiceDayUtcMs(isoOrDate);
  if (dayMs === null) return false;
  return dayMs < getTodayUtcMs();
};

export const isServiceDateOnOrAfterToday = (isoOrDate) => {
  const dayMs = getServiceDayUtcMs(isoOrDate);
  if (dayMs === null) return false;
  return dayMs >= getTodayUtcMs();
};

/** Etiqueta de mes/año para selectores (ej. "Mayo 2025") */
export const formatMonthYearLabel = (year, month, locale = "es-MX") => {
  const d = new Date(Date.UTC(year, month - 1, 1));
  const raw = d.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};
